import { HttpError } from '@littlebird/auth-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { subscriptionStore } from '@/features/subscription/subscription.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import {
  fetchNotesStore,
  notesStoreQueryKey,
  reportNoteError,
  saveNotesText,
} from './assistant-notes.query';

import type { NotesStore } from './assistant-notes.types';

const NOTES_MAX_LENGTH_INTERNAL = 12000;
const NOTES_MAX_LENGTH_PAID = 12000;
const NOTES_MAX_LENGTH_FREE = 3000;

function getNotesMaxLength(): number {
  if (userStore.isInternalUser) {
    return NOTES_MAX_LENGTH_INTERNAL;
  }
  if (subscriptionStore.isPlus) {
    return NOTES_MAX_LENGTH_PAID;
  }
  return NOTES_MAX_LENGTH_FREE;
}

/**
 * The text area of a store kept as text: a draft over the saved text, the character cap, the
 * save, and the hand-over to the list when the store converted while the user typed.
 */
export function useNotesTextEditor(savedText: string) {
  const queryClient = useQueryClient();
  // null while the field shows the saved text, so a save from elsewhere shows until the user types
  const [draft, setDraft] = useState<string | null>(null);
  const [changedWhileEditing, setChangedWhileEditing] = useState(false);
  const maxLength = getNotesMaxLength();
  const text = draft ?? savedText;
  const dirty = draft !== null && draft !== savedText;

  const save = useMutation({
    mutationFn: saveNotesText,
    // The account that sent the save. A save that returns after another account signed in
    // must not reach that account's notes or toasts
    onMutate: () => userStore.userId,
    onSuccess: (_, savedDraft, senderId) => {
      if (senderId !== userStore.userId) return;
      queryClient.setQueryData<NotesStore>(notesStoreQueryKey(), {
        mode: 'file',
        text: savedDraft,
      });
      setDraft(null);
      trackEvent('CHAT_SETTING_PROMPT_UPDATED');
    },
    onError: async (error, _, senderId) => {
      if (senderId !== userStore.userId) return;
      if (error instanceof HttpError && error.status === 409) {
        // A 409 is a store that converted to items or a store another writer holds; the mode
        // tells them apart. Fetched outside the cache, so the text area stays with the typed
        // text until the user dismisses the message
        const store = await fetchNotesStore().catch(() => null);
        if (store?.mode === 'items') {
          setChangedWhileEditing(true);
          return;
        }
      }
      reportNoteError(error, "Couldn't save your notes. Try again.");
    },
  });

  return {
    text,
    dirty,
    maxLength,
    changedWhileEditing,
    isSaving: save.isPending,
    setText: (next: string) => {
      if (next.length <= maxLength) {
        setDraft(next);
      }
    },
    save: () => {
      if (dirty && !changedWhileEditing) {
        save.mutate(text);
      }
    },
    discard: () => setDraft(null),
    reload: () => {
      setChangedWhileEditing(false);
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: notesStoreQueryKey() });
    },
  };
}
